/* eslint-disable rxjs/no-exposed-subjects */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';

import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { CompletionLengthEnum } from '../../../../api/ai/enums/completion-length.enum';
import { CompletionToneEnum } from '../../../../api/ai/enums/completion-tone.enum';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { AiSettingFormModel } from '../../models/ai-setting-form.model';
import { AiQuoteModel } from '../../models/ai-quote.model';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ResizeDirectionEnum } from '../TextareaInput/textarea-input.component';
import { AiQuickTipEnums } from '../../enums/ai-quick-tip.enum';
import { promptLanguageOptions } from '../../data/prompt-language-options';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CompletionTargetAudienceEnum } from '../../../../api/ai/enums/completion-target-audience.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CompletionPurposeEnum } from '../../../../api/ai/enums/completion-purpose.enum';
import { languageDataLocalizationMap } from '../../../../Core/Localization/language-localization.model';

@Component({
    selector: 'agpr-ai-setting-form',
    templateUrl: './AI-setting-form.component.html',
    styleUrls: ['./AI-setting-form.component.scss'],
    providers: [getProvider(AISettingFormComponent)],
    standalone: false
})
export class AISettingFormComponent extends ControlValueAccessorModel<AiSettingFormModel> implements OnInit, OnDestroy {
    public readonly MAX_QUOTES = 3;
    public readonly MAX_KEY_MESSAGES = 10;
    readonly buttonEnum = ButtonTypesEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly aiQuickTipEnums = AiQuickTipEnums;

    @Input() headingLevel = 2;
    @Input() allowCustomOptions: boolean = false;
    @Input() showTargetAudienceField: boolean = false;
    @Input() showPurposeField: boolean = false;

    @Output() aiQuickTip = new EventEmitter<AiQuickTipEnums>();

    selectedLengthOption: DropdownOptionModel;
    selectedLanguageOption: DropdownOptionModel;
    selectedToneOption: DropdownOptionModel;
    selectedFormatOption: DropdownOptionModel;
    selectedTargetAudienceOption: DropdownOptionModel;
    selectedPurposeOption: DropdownOptionModel;

    lengthOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Common.dialog.aiHelper.pressReleaseLength.short', CompletionLengthEnum.SHORT),
        new DropdownOptionModel('Common.dialog.aiHelper.pressReleaseLength.average', CompletionLengthEnum.AVERAGE),
        new DropdownOptionModel('Common.dialog.aiHelper.pressReleaseLength.long', CompletionLengthEnum.LONG),
    ];

    languageOptions: DropdownOptionModel[] = promptLanguageOptions;
    languageOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject([]);

    toneOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.professional', CompletionToneEnum.PROFESSIONAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.formal', CompletionToneEnum.FORMAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.informal', CompletionToneEnum.INFORMAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.fun', CompletionToneEnum.FUN),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.friendly', CompletionToneEnum.FRIENDLY),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.optimistic', CompletionToneEnum.OPTIMISTIC),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.assertive', CompletionToneEnum.ASSERTIVE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.cooperative', CompletionToneEnum.COOPERATIVE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.sincere', CompletionToneEnum.SINCERE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.humorous', CompletionToneEnum.HUMOROUS),
    ];
    toneOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject([]);

    targetAudienceOptions: DropdownOptionModel[] = [];
    targetAudienceOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject([]);

    purposeOptions: DropdownOptionModel[] = [];
    purposeOptions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject([]);

    formatOptions: DropdownOptionModel[];

    subscriptions: Subscription[] = [];

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService
    ) {
        super();
        this.subscriptions.push(
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.languageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            })
        );
    }

    ngOnInit(): void {
        this.setAutocompleteOptions();
        this.setSelectedOptions();
    }

    setAutocompleteOptions(): void {
        if (this.allowCustomOptions) {
            this.languageOptions.forEach(x => x.label = languageDataLocalizationMap[x.label]);
            this.languageOptions$.next(this.languageOptions);
            this.toneOptions.forEach(x => x.label = this.localizationService.transform(x.label));
            this.toneOptions$.next(this.toneOptions);
        }
        if (this.showTargetAudienceField) {
            Object.values(CompletionTargetAudienceEnum).filter(x => x !== CompletionTargetAudienceEnum.CUSTOM).forEach(targetAudience => {
                this.targetAudienceOptions.push(new DropdownOptionModel(
                    this.localizationService.transform('Common.dialog.aiHelper.targetAudience.' + targetAudience),
                    targetAudience
                ));
            });
            this.targetAudienceOptions$.next(this.targetAudienceOptions);
        }
        if (this.showPurposeField) {
            Object.values(CompletionPurposeEnum).filter(x => x !== CompletionPurposeEnum.CUSTOM).forEach(purpose => {
                this.purposeOptions.push(new DropdownOptionModel(
                    this.localizationService.transform('Common.dialog.aiHelper.purpose.' + purpose),
                    purpose
                ));
            });
            this.purposeOptions$.next(this.purposeOptions);
        }
    }

    writeValue(value: AiSettingFormModel): void {
        this.value = value;
        if (!value)  {
            return;
        }
        this.formatOptions = this.value.formatConfig.formatOptions;
        this.setSelectedOptions();
    }

    setSelectedOptions(): void {
        if (!this.value) {
            return;
        }
        this.selectedLengthOption = this.lengthOptions.find(option => option.value === this.value.length);
        this.selectedLanguageOption = this.languageOptions.find(option => option.value === this.value.language);
        this.selectedToneOption = this.toneOptions.find(option => option.value === this.value.tone);
        this.selectedFormatOption = this.value.formatConfig.formatOptions.find(option => option.value === this.value.formatConfig.defaultFormat);
        this.selectedTargetAudienceOption = this.targetAudienceOptions.find(option => option.value === this.value.targetAudience);
        this.selectedPurposeOption = this.purposeOptions.find(option => option.value === this.value.purpose);
    }

    addKeyMessage(): void {
        this.value.keyMessages.push('');
        this.onModelChange(this.value);
    }

    removeMessage(index: number) {
        this.value.keyMessages.splice(index, 1);
        this.onModelChange(this.value);
    }

    updateKeyMessage(event, index: number) {
        this.value.keyMessages[index] = event;
        this.onModelChange(this.value);
    }

    addQuote(): void {
        this.value.quotes.push(new AiQuoteModel());
        this.onModelChange(this.value);
    }

    trackByIndex(index: number, obj: any): any {
        return index;
    }

    showQuickTip(type: AiQuickTipEnums) {
        this.aiQuickTip.next(type);
        this.onModelChange(this.value);
    }

    removeQuote(index: number) {
        this.value.quotes.splice(index, 1);
        this.onModelChange(this.value);
    }

    formatChange(): void {
        this.value.formatConfig.defaultFormat = this.selectedFormatOption.value;
        this.onModelChange(this.value);
    }

    toneChange(toneOption: DropdownOptionModel | string): void {
        if (this.allowCustomOptions) {
            if (toneOption instanceof DropdownOptionModel) {
                this.value.tone = toneOption.value;
                this.value.customTone = null;
            } else {
                this.value.tone = CompletionToneEnum.CUSTOM;
                this.value.customTone = toneOption;
            }
        } else {
            this.value.tone = this.selectedToneOption.value;
        }
        this.onModelChange(this.value);
    }

    lengthChange(): void {
        this.value.length = this.selectedLengthOption.value;
        this.onModelChange(this.value);
    }

    languageChange(languageOption: DropdownOptionModel | string): void {
        if (this.allowCustomOptions) {
            if (languageOption instanceof DropdownOptionModel) {
                this.value.language = languageOption.value;
                this.value.customLanguage = null;
            } else {
                this.value.language = null;
                this.value.customLanguage = languageOption;
            }
        } else {
            this.value.language = this.selectedLanguageOption.value;
        }
        this.onModelChange(this.value);
    }

    targetAudienceSelected(targetAudienceOption: DropdownOptionModel | string): void {
        if (targetAudienceOption instanceof DropdownOptionModel) {
            this.value.targetAudience = targetAudienceOption.value;
            this.value.customTargetAudience = null;
        } else {
            this.value.targetAudience = CompletionTargetAudienceEnum.CUSTOM;
            this.value.customTargetAudience = targetAudienceOption;
        }
        this.onModelChange(this.value);
    }

    purposeSelected(purposeOption: DropdownOptionModel | string): void {
        if (purposeOption instanceof DropdownOptionModel) {
            this.value.purpose = purposeOption.value;
            this.value.customPurpose = null;
        } else {
            this.value.purpose = CompletionPurposeEnum.CUSTOM;
            this.value.customPurpose = purposeOption;
        }
        this.onModelChange(this.value);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
